import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import {
  IonButton,
  IonSpinner,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonSkeletonText
} from '@ionic/react';
import {
  logoGithub,
  sparklesOutline
} from 'ionicons/icons';
import { getDefaultModelForAgent } from '../../constants/aiModels';
import CollapsibleCard from '../../components/atoms/CollapsibleCard';
import AgentModelFields, { isClaudeOAuthToken } from '../../components/molecules/AgentModelFields/AgentModelFields';
import {
  useAgentOptions, findAgentOptionValue, parseAgentOption, firstOptionForAgent,
} from '../../utils/agent-options';
import api from '../../services/api';
import './ProjectSettings.css';

const gitlabLogoSvg = 'data:image/svg+xml,' + encodeURIComponent('<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.452.045 13.587a.924.924 0 00.331 1.023L12 23.054l11.624-8.443a.92.92 0 00.331-1.024" fill="#E24329"/><path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#FC6D26"/><path d="M12 23.054l-4.418-13.6H1.386L12 23.054z" fill="#FCA326"/><path d="M1.386 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.386 9.452z" fill="#E24329"/><path d="M12 23.054l4.418-13.6h6.196L12 23.054z" fill="#FCA326"/><path d="M22.614 9.452l1.341 4.135a.924.924 0 01-.331 1.023L12 23.054l10.614-13.602z" fill="#E24329"/></svg>');

// Each AI vendor owns EXACTLY ONE project-secret slot — claude is dual-shape
// (aiApiKey for sk-ant-api03-…, aiOAuthToken for sk-ant-oat01-…), codex →
// codexApiKey, gemini → geminiApiKey. This 1:1 mapping (matching what the deploy
// path reads per-agent) is what lets every configured agent round-trip on save
// with no phantom duplicates and nothing clobbered. `isPrimary` lets a NON-claude
// DEFAULT agent fall back to the legacy generic aiApiKey slot for pre-per-vendor
// projects, so an older codex/gemini default still shows its saved key.
const vendorKeyFromSecrets = (secrets, vendor, isPrimary) => {
  const s = secrets || {};
  const str = (x) => ((typeof x === 'string' && x) ? x : '');
  if (vendor === 'codex') return str(s.codexApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  if (vendor === 'gemini') return str(s.geminiApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  return str(s.aiOAuthToken) || str(s.aiApiKey); // claude
};

const ProjectSettings = () => {
  const { projectId } = useParams();
  const { project, setToast, refreshProject } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expandSection = searchParams.get('section');
  
  const [saving, setSaving] = useState(false);
  const [githubRepos, setGithubRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [hasLoadedRepos, setHasLoadedRepos] = useState(false);
  
  const [gitlabRepos, setGitlabRepos] = useState([]);
  const [loadingGitlabRepos, setLoadingGitlabRepos] = useState(false);
  const [hasLoadedGitlabRepos, setHasLoadedGitlabRepos] = useState(false);
  
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [selectedGitlabRepos, setSelectedGitlabRepos] = useState([]);
  
  const [aiAgent, setAiAgent] = useState('claude');
  const [aiModel, setAiModel] = useState('auto');
  const [customApiKey, setCustomApiKey] = useState('');
  // Claude only — separate field for Pro/Max subscription OAuth token
  // (sk-ant-oat01-…). User can fill EITHER api key OR oauth; runtime
  // prefers oauth when both present.
  const [customOAuthToken, setCustomOAuthToken] = useState('');

  // Shared atomic form uses a combined `agent:model` value. Map our separate
  // aiAgent/aiModel state to/from it so the SAME component renders here and in
  // the deploy modal / pipeline editor.
  const { options: agentOptions } = useAgentOptions();
  const aiSettingsValue = findAgentOptionValue({ aiAgent, aiModel }, agentOptions);

  // Additional agent credential rows added via "Add another agent". Each row is
  // a full model+key picker — the SAME dropdown that already lists every vendor
  // + model — so the user picks whatever type they want per row (no per-vendor
  // buttons). Each row's key persists to its vendor's project-secret slot on save.
  const [extraRows, setExtraRows] = useState([]);

  const [secrets] = useState({
    npmToken: '',
    gemToken: '',
    mavenUsername: '',
    mavenPassword: '',
    pipToken: ''
  });
  
  // OWNER-ONLY page. Project settings (integrations, selected repos, AI
  // credentials) belong to the project owner alone; invited members must not
  // reach this page. `project.isOwner` is set authoritatively by the backend
  // (GET /projects/:id). While the project is still loading it's undefined —
  // only redirect once we KNOW the viewer is a non-owner (=== false), so the
  // owner never sees a flash-redirect. The backend also 403s the write paths,
  // so this is UX; it isn't the security boundary.
  useEffect(() => {
    if (project && project.isOwner === false) {
      setToast?.({
        show: true,
        message: 'Only the project owner can view project settings.',
        color: 'warning',
      });
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [project, projectId, navigate, setToast]);

  useEffect(() => {
    if (project) {
      if (project.githubRepos && Array.isArray(project.githubRepos)) {
        setSelectedRepos(project.githubRepos);
      } else if (project.githubRepo) {
        setSelectedRepos([project.githubRepo]);
      } else {
        setSelectedRepos([]);
      }
      
      if (project.gitlabRepos && Array.isArray(project.gitlabRepos)) {
        setSelectedGitlabRepos(project.gitlabRepos);
      } else {
        setSelectedGitlabRepos([]);
      }
      
      const primaryVendor = project.aiAgent || 'claude';
      setAiAgent(primaryVendor);
      setAiModel(project.aiModel || 'auto');

      // Seed the PRIMARY picker's key from the default agent's OWN vendor slot
      // (claude→aiOAuthToken/aiApiKey, codex→codexApiKey, gemini→geminiApiKey).
      // All are masked in the GET, so a stored cred still shows "✓ Saved".
      setCustomApiKey(vendorKeyFromSecrets(project.secrets, primaryVendor, true));
      setCustomOAuthToken('');
    }
  }, [project, projectId]);

  // Seed the "Add another agent" rows from EVERY vendor other than the primary
  // that already has a saved key in its own slot. Each vendor owns exactly one
  // slot, so this reconstructs the saved set 1:1 — no phantom duplicates, and a
  // saved claude/codex/gemini extra always comes back on reload (masked, "✓
  // Saved"). Reset (not append) so the rows exactly mirror what's persisted.
  useEffect(() => {
    if (!project) return;
    const sec = project.secrets || {};
    const primaryVendor = project.aiAgent || 'claude';
    const rows = [];
    for (const vendor of ['claude', 'codex', 'gemini']) {
      if (vendor === primaryVendor) continue;
      const k = vendorKeyFromSecrets(sec, vendor, false);
      if (k) rows.push({ value: firstOptionForAgent(agentOptions, vendor), apiKey: k });
    }
    setExtraRows(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, agentOptions.length]);

  const loadGithubRepos = async () => {
    if (hasLoadedRepos) return;
    
    setLoadingRepos(true);
    try {
      // Scope to THIS project so the backend returns the OWNER's repos (never
      // the viewer's own account's).
      const data = await api.get(`/github/repos?projectId=${encodeURIComponent(projectId)}`);
      console.log('📦 GitHub repos loaded:', data.repos?.length || 0, 'repos');
      const loaded = data.repos || [];
      setGithubRepos(loaded);
      // Only LOCK the load when we actually got repos. An empty result — a
      // transient backend error swallowed to [], GitHub not yet connected, or
      // a race right after reconnect — must stay retryable. Otherwise the very
      // first empty/failed fetch sticks for the whole session and even
      // reconnecting GitHub can't recover it without a full page reload (the
      // bug: re-expanding the panel short-circuits on hasLoadedRepos and never
      // re-hits /github/repos).
      if (loaded.length > 0) setHasLoadedRepos(true);
      
      // Only warn when the token EXPIRED (GitHub WAS connected). A
      // not-connected response (needsSetup) is conveyed by the inline
      // "connect in Integrations" message — no toast for that.
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'Only public repositories visible. Reconnect GitHub to access private repos.',
          color: 'warning',
          duration: 8000
        });
      }
    } catch (error) {
      console.error('❌ Error loading GitHub repos:', error);
      setGithubRepos([]);
      // Do NOT mark loaded on error — leave hasLoadedRepos false so the next
      // expand retries instead of permanently showing "No repositories".
      // Don't toast when GitHub simply isn't connected (auth / not-found) — the
      // inline "connect in Integrations" message already says so. Only a genuine
      // server / network failure surfaces a toast.
      const status = error?.status || error?.response?.status;
      if (!status || status >= 500) {
        setToast({
          show: true,
          message: 'Failed to load GitHub repositories. Please try again.',
          color: 'danger'
        });
      }
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleRepoToggle = (repoFullName) => {
    setSelectedRepos(prev => {
      if (prev.includes(repoFullName)) {
        return prev.filter(r => r !== repoFullName);
      } else {
        return [...prev, repoFullName];
      }
    });
  };

  const loadGitlabRepos = async () => {
    if (hasLoadedGitlabRepos) return;
    
    setLoadingGitlabRepos(true);
    try {
      // Scope to THIS project so the backend returns the OWNER's repos (never
      // the viewer's own account's).
      const data = await api.get(`/gitlab/repos?projectId=${encodeURIComponent(projectId)}`);
      console.log('📦 GitLab repos loaded:', data.repos?.length || 0, 'repos');
      setGitlabRepos(data.repos || []);
      setHasLoadedGitlabRepos(true);
      
      // Only warn when a token EXPIRED (GitLab WAS connected). A not-connected
      // response (needsSetup) is conveyed by the inline "connect in
      // Integrations" message — no toast for that.
      if (data.needsReconnect) {
        setToast({
          show: true,
          message: data.message || 'GitLab token expired. Please reconnect GitLab.',
          color: 'warning',
          duration: 8000
        });
      }
    } catch (error) {
      console.error('Error loading GitLab repos:', error);
      setGitlabRepos([]);
      setHasLoadedGitlabRepos(true);
      // Don't toast when GitLab simply isn't connected (auth / not-found) — the
      // inline "connect in Integrations" message already says so. Only a genuine
      // server / network failure surfaces a toast.
      const status = error?.status || error?.response?.status;
      if (!status || status >= 500) {
        setToast({
          show: true,
          message: 'Failed to load GitLab repositories. Please try again.',
          color: 'danger'
        });
      }
    } finally {
      setLoadingGitlabRepos(false);
    }
  };

  // A selected entry matches a list repo by full_name AND (when present)
  // integrationId — so the same path on a cloud vs a self-hosted instance are
  // distinct. Legacy selections (no integrationId stored) match by name only.
  const gitlabRepoMatches = (selected, repo) => {
    const selName = typeof selected === 'string' ? selected : selected.full_name;
    if (selName !== repo.full_name) return false;
    return !selected.integrationId || selected.integrationId === repo.integrationId;
  };

  const handleGitlabRepoToggle = (repo) => {
    setSelectedGitlabRepos(prev => {
      if (prev.some(r => gitlabRepoMatches(r, repo))) {
        return prev.filter(r => !gitlabRepoMatches(r, repo));
      }
      return [...prev, {
        full_name: repo.full_name,
        default_branch: repo.default_branch || 'main',
        // Pin which connected instance this repo belongs to so reviews/MRs route
        // to the right GitLab and a same-named repo on the other instance stays
        // separate.
        integrationId: repo.integrationId,
        instanceHost: repo.instanceHost,
      }];
    });
  };

  const persistSettings = async (overrides = {}) => {
    setSaving(true);
    try {
      const { analysisGraphConfig: _graphOverride, ...restOverrides } = overrides;

      // Primary picker = the project DEFAULT agent (aiAgent/aiModel + the
      // canonical aiApiKey/aiOAuthToken slot the runtime reads). Each "Add
      // another" row is just another key for whatever vendor its dropdown picks,
      // so a deployed multi-engine agent finds each vendor's credential.
      const primaryAgent = aiAgent;
      const primaryModel = aiModel;

      const updatePayload = {
        githubRepos: selectedRepos,
        gitlabRepos: selectedGitlabRepos,
        aiAgent: primaryAgent,
        aiModel: primaryModel,
        ...restOverrides
      };

      const secretsToUpdate = {};
      // Route a claude credential by prefix into aiApiKey vs aiOAuthToken.
      // Untouched mask ('...') = keep — skip it.
      const setClaudeKey = (raw) => {
        if (typeof raw !== 'string' || raw.includes('...')) return;
        const v = raw.trim();
        if (v === '') { secretsToUpdate.aiApiKey = null; secretsToUpdate.aiOAuthToken = null; }
        else if (isClaudeOAuthToken(v)) { secretsToUpdate.aiOAuthToken = v; secretsToUpdate.aiApiKey = null; }
        else { secretsToUpdate.aiApiKey = v; secretsToUpdate.aiOAuthToken = null; }
      };
      const setPerAgentKey = (field, raw) => {
        if (typeof raw !== 'string' || raw.includes('...')) return;
        const v = raw.trim();
        secretsToUpdate[field] = v === '' ? null : v;
      };
      // Route a {vendor, key} to its project-secret slot (claude is dual-shape).
      const setVendorKey = (vendor, raw) => {
        if (vendor === 'codex') setPerAgentKey('codexApiKey', raw);
        else if (vendor === 'gemini') setPerAgentKey('geminiApiKey', raw);
        else setClaudeKey(raw);
      };

      // Primary picker's key + one per "Add another" row → each vendor's OWN
      // slot (last write wins on dupes). NO cross-vendor mirror into aiApiKey:
      // that overload made a non-claude primary and a claude row fight over the
      // same field, so the claude row was silently wiped on save. The deploy
      // path resolves each agent per-vendor (codex→codexApiKey, gemini→
      // geminiApiKey, claude→aiApiKey/aiOAuthToken), so per-vendor slots are all
      // the runtime needs — see backend agent-key.js + marketplace.js snapshot.
      setVendorKey(primaryAgent, customApiKey);
      for (const row of extraRows) {
        const p = parseAgentOption(row.value || '');
        if (p.aiAgent) setVendorKey(p.aiAgent, row.apiKey);
      }
      Object.entries(secrets).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          secretsToUpdate[key] = value;
        }
      });
      if (Object.keys(secretsToUpdate).length > 0) {
        updatePayload.secrets = secretsToUpdate;
      }

      await api.put(`/projects/${projectId}`, updatePayload);
      // Re-fetch the project so the form re-seeds from the saved state (masked
      // key + "✓ Saved") without a manual page refresh.
      await refreshProject?.();
      setToast({
        show: true,
        message: 'Settings saved successfully!',
        color: 'success'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({
        show: true,
        message: 'Failed to save settings',
        color: 'danger'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persistSettings();

  // Clear AI credentials — deletes them from the DB IMMEDIATELY (one click,
  // no separate Save). PUTs aiApiKey/aiOAuthToken = null; the backend maps a
  // null secret to `delete mergedSecrets[field]`, so the encrypted value is
  // actually removed from the project row (not blanked). Only `secrets` is in
  // the payload, so name / repos / agent are untouched.
  const handleClearCredentials = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}`, {
        secrets: { aiApiKey: null, aiOAuthToken: null },
      });
      setCustomApiKey('');
      setCustomOAuthToken('');
      await refreshProject?.();
      setToast({ show: true, message: 'AI credentials deleted', color: 'success' });
    } catch (err) {
      console.error('Error clearing AI credentials:', err);
      setToast({ show: true, message: 'Failed to delete AI credentials', color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  if (!project) {
    return (
      <div className="project-settings-page">
        <div className="settings-content">
          {[0, 1, 2].map((i) => (
            <div className="collapsible-card settings-skeleton-card" key={i} aria-hidden="true">
              <div className="collapsible-card-header">
                <div className="collapsible-card-header-left">
                  <IonSkeletonText animated className="settings-skeleton-chevron" />
                  <div className="collapsible-card-icon">
                    <IonSkeletonText animated className="settings-skeleton-icon" />
                  </div>
                  <div className="collapsible-card-title-group">
                    <IonSkeletonText animated className="settings-skeleton-title" />
                    <IonSkeletonText animated className="settings-skeleton-subtitle" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="project-settings-page">

      <div className="settings-content">
        {/* GitHub Integration */}
        <CollapsibleCard
          title="GitHub Repository"
          subtitle="Select GitHub repositories for this project. AI agents will create pull requests here."
          icon={logoGithub}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedRepos) {
              loadGithubRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {githubRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitHub in{' '}
                  <button type="button" className="link-inline" onClick={() => navigate('/integrations')}>Integrations</button>.
                </p>
              ) : (
                githubRepos.map((repo) => (
                  <IonItem key={repo.full_name} className="repo-item">
                    <IonCheckbox
                      checked={selectedRepos.includes(repo.full_name)}
                      onIonChange={() => handleRepoToggle(repo.full_name)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* GitLab Integration */}
        <CollapsibleCard
          title="GitLab Repository"
          subtitle="Select GitLab repositories for this project. AI agents will create merge requests here."
          icon={gitlabLogoSvg}
          defaultExpanded={false}
          lazyLoad={true}
          showHeaderActionsOnlyWhenExpanded={true}
          onToggle={(isExpanded) => {
            if (isExpanded && !hasLoadedGitlabRepos) {
              loadGitlabRepos();
            }
          }}
          headerActions={
            <IonButton
              fill="clear"
              size="small"
              className="btn-white"
              onClick={handleSave}
              disabled={saving || loadingGitlabRepos}
            >
              Save
            </IonButton>
          }
        >
          {loadingGitlabRepos ? (
            <div className="repos-loading">
              <IonSpinner />
              <p>Loading repositories...</p>
            </div>
          ) : (
            <div className="repos-list">
              {gitlabRepos.length === 0 ? (
                <p className="no-repos">No repositories available. Please connect GitLab in{' '}
                  <button type="button" className="link-inline" onClick={() => navigate('/integrations')}>Integrations</button>.
                </p>
              ) : (
                gitlabRepos.map((repo) => (
                  <IonItem key={`${repo.integrationId || ''}:${repo.full_name}`} className="repo-item">
                    <IonCheckbox
                      checked={selectedGitlabRepos.some(r => gitlabRepoMatches(r, repo))}
                      onIonChange={() => handleGitlabRepoToggle(repo)}
                      slot="start"
                    />
                    <IonLabel>
                      <h3>{repo.full_name}</h3>
                      {repo.instanceHost && <p className="repo-instance">{repo.instanceHost}</p>}
                    </IonLabel>
                    {repo.private && <span className="repo-badge" slot="end">Private</span>}
                  </IonItem>
                ))
              )}
            </div>
          )}
        </CollapsibleCard>

        {/* AI Settings */}
        <CollapsibleCard
          title="AI Settings"
          subtitle="Configure the AI agent and model used for code generation and test automation."
          icon={sparklesOutline}
          defaultExpanded={expandSection === 'ai'}
          showHeaderActionsOnlyWhenExpanded={true}
          headerActions={
            <>
              <IonButton
                fill="clear"
                size="small"
                className="btn-white"
                onClick={handleClearCredentials}
                disabled={saving}
              >
                Clear
              </IonButton>
              <IonButton
                fill="clear"
                size="small"
                className="btn-white"
                onClick={handleSave}
                disabled={saving}
              >
                Save
              </IonButton>
            </>
          }
        >
          <div className="ai-settings-form">
            {/* Primary agent = the project default. The SAME atomic picker used
                in the deploy modal + pipeline editor; its dropdown already lists
                EVERY vendor + model (Claude / Codex / Gemini). */}
            <AgentModelFields
              agentOptions={agentOptions}
              value={aiSettingsValue}
              onValueChange={(v) => {
                const p = parseAgentOption(v);
                const nextAgent = p.aiAgent || 'claude';
                setAiAgent(nextAgent);
                setAiModel(p.aiModel || getDefaultModelForAgent(nextAgent));
              }}
              apiKey={customApiKey}
              onApiKeyChange={setCustomApiKey}
              disabled={saving}
              idPrefix="project-ai"
            />

            {/* Additional agents — the SAME full picker per row. The user picks
                any type from the dropdown (no per-vendor buttons); each row's key
                saves to its vendor's project-secret slot. */}
            {extraRows.map((row, i) => (
              <div key={i} className="ai-settings-extra-row">
                <div className="ai-settings-extra-header">
                  <button
                    type="button"
                    className="ai-settings-remove-btn"
                    onClick={() => setExtraRows((rows) => rows.filter((_, j) => j !== i))}
                    disabled={saving}
                  >
                    Remove
                  </button>
                </div>
                <AgentModelFields
                  agentOptions={agentOptions}
                  value={row.value}
                  onValueChange={(v) => setExtraRows((rows) => rows.map((r, j) => (j === i ? { ...r, value: v } : r)))}
                  apiKey={row.apiKey}
                  onApiKeyChange={(k) => setExtraRows((rows) => rows.map((r, j) => (j === i ? { ...r, apiKey: k } : r)))}
                  disabled={saving}
                  idPrefix={`project-ai-extra-${i}`}
                />
              </div>
            ))}

            <div className="ai-settings-add-agent">
              <button
                type="button"
                className="ai-settings-add-btn"
                onClick={() => setExtraRows((rows) => [...rows, { value: '', apiKey: '' }])}
                disabled={saving}
              >
                + Add another agent
              </button>
            </div>
          </div>
        </CollapsibleCard>

      </div>
    </div>
  );
};

export default ProjectSettings;
